import { useMemo } from 'react';
import { AuctionPanel } from '../table-session/AuctionPanel';
import { choiceAsText, conceptualBidComment, conceptualCallFormatter, isManyWayChoice } from './conceptualCalls';
import type { AuctionBid } from '../../types/problems';

interface ConceptualAuctionTableProps {
  auction: AuctionBid[];
  vulnerability: string;
  decisionSeat: 'N' | 'E' | 'S' | 'W';
  /**
   * The problem on screen. Only ever used to rewind the auction to its first bid when the editor
   * steps to another problem — it is never displayed here.
   */
  serial: number;
}

/**
 * The auction display for a conceptual problem (feature 029).
 *
 * <p>It IS the shared table-session <code>AuctionPanel</code> — the same seats, rounds, dealer
 * rotation, alert and comment tooltips, the same cream sticky header on the felt table's panel, and
 * the same scroll rig (the chevron hint, and the rewind to the first bid) — configured for the two
 * call shapes that appear in the conceptual data and nowhere else. It is a wrapper rather than a
 * fork on purpose: the constitution's Bid Alert Display standard forbids a bespoke auction
 * renderer, and a second copy of this table would drift from the original exactly as duplicated
 * display logic always has here.</p>
 *
 * <p>Everything conceptual about it lives in <code>conceptualCalls</code>, which delegates every
 * call it does not recognise back to the shared defaults. Passing neither resolver would render
 * this auction exactly as the Bidding Practice page renders one.</p>
 */
export function ConceptualAuctionTable({
  auction,
  vulnerability,
  decisionSeat,
  serial,
}: ConceptualAuctionTableProps) {
  const annotated = useMemo(() => withChoiceAnnotations(auction), [auction]);

  return (
    <AuctionPanel
      auction={annotated}
      vulnerability={vulnerability}
      decisionSeat={decisionSeat}
      // The conceptual data carries its OWN decision marker ('?' or '??', normalised server-side),
      // so a derived one would render a second question mark in the auction.
      pendingSeat={null}
      // One trigger, and its length is constant across renders as the panel requires.
      rewindOn={[serial]}
      bidCommentResolver={conceptualBidComment}
      callFormatter={conceptualCallFormatter}
      // The authoring schema REQUIRES an annotation on a wildcard, and that annotation is the only
      // thing saying what the wildcard stands for — so this page is the one caller that cannot
      // have it dropped (FR-017a). See the prop's note on AuctionPanel for why it is opt-in.
      showCallAnnotations
    />
  );
}

/**
 * Put the whole choice into the tooltip of every call the cell has to draw as `*` (FR-017b).
 *
 * <p><b>Why a pre-map and not a resolver.</b> `AuctionTable` reads an annotation as
 * <code>bid.comment || bidCommentResolver(call)</code> — an authored comment WINS, and the
 * resolver is never consulted for a call that has one. A four-way choice carrying an annotation
 * therefore has no way to show both through that channel, and the 2026-09-10 clarification is
 * explicit that neither may be dropped. Rewriting the comment here, in the one component that
 * knows this auction is conceptual, costs nothing and cannot reach any other page — which is the
 * whole of FR-017d. Adding an override prop to the shared table would have been the alternative,
 * and it would change shared behaviour for one caller's benefit.</p>
 *
 * <p>The choice goes first and the author's own annotation beneath it, separated by a newline —
 * the convention <code>combineBidAnnotation</code> already uses for an alert above a comment, and
 * which the tooltip already renders with <code>whitespace-pre-line</code>.</p>
 *
 * <p>Every other call is passed through by identity, so an auction with no many-way choice in it —
 * which today is all of them — reaches the panel as exactly the array it arrived as.</p>
 */
function withChoiceAnnotations(auction: AuctionBid[]): AuctionBid[] {
  if (!auction.some((bid) => isManyWayChoice(bid.call))) return auction;

  return auction.map((bid) => {
    if (!isManyWayChoice(bid.call)) return bid;

    const choice = choiceAsText(bid.call);
    const authored = bid.comment?.trim();

    return { ...bid, comment: authored ? `${choice}\n${authored}` : choice };
  });
}
